import { BasisTheoryClient } from '@basis-theory/node-sdk'
import axios from 'axios'

import { getBasisTheoryPublicKey } from '../config/config'
import { detectBinaryContentType, detectContentType } from '../helpers/utils'

let btClient: BasisTheoryClient | undefined = undefined

export function getBtClient() {
  if (!btClient) {
    btClient = new BasisTheoryClient({
      apiKey: getBasisTheoryPublicKey(),
    })
  }

  return btClient
}

export async function createToken(value: string) {
  if (process.env.REACT_APP_INTEGRATION) {
    return `token_${value}`
  }

  const client = getBtClient()

  const token = await client.tokens.create({
    type: 'token',
    data: value,
  })

  if (!token?.id) {
    throw new Error('Failed to create token: No token ID returned')
  }

  return token.id
}

export async function revealToken(token: string): Promise<any> {
  if (process.env.REACT_APP_INTEGRATION) {
    return `encrypted_${token}`
  }

  const client = getBtClient()

  // Create a session for secure token retrieval
  const { sessionKey, nonce } = await client.sessions.create()

  try {
    // Authorize the session with your backend
    await axios.post(`/api/v1/console/encrypted-tokens/authorize-session/`, { nonce, token })
  } catch (error) {
    console.error(error)
    return new Error('Failed to authorize session')
  }

  // Create a new client instance with the session key for secure retrieval
  const sessionClient = new BasisTheoryClient({
    apiKey: sessionKey,
  })

  // Retrieve the token data using the session client
  const value = await sessionClient.tokens.get(token)

  return value?.data
}

export async function createDocumentToken(value: string | File) {
  if (process.env.REACT_APP_INTEGRATION) {
    return `document_token_${typeof value === 'string' ? value : value.name}`
  }

  const client = getBtClient()

  // If it's a File object, use it directly for multipart upload
  if (value instanceof File) {
    const document = await client.documents.upload({
      document: value,
      request: {
        metadata: {
          content_type: value.type,
          original_filename: value.name,
          created_by: 'application',
        },
      },
    })

    if (!document?.id) {
      throw new Error('Failed to create document token: No document ID returned')
    }

    return document.id
  }

  // If it's a string, detect content type and create appropriate blob
  const detectedType = detectContentType(value)
  const blob = new Blob([value], { type: detectedType })

  const document = await client.documents.upload({
    document: blob,
    request: {
      metadata: {
        content_type: detectedType, // Use detected content type
        created_by: 'application',
      },
    },
  })

  if (!document?.id) {
    throw new Error('Failed to create document token: No document ID returned')
  }

  return document.id
}

export async function revealDocumentToken(documentId: string) {
  if (process.env.REACT_APP_INTEGRATION) {
    // Return mock PDF content as ArrayBuffer
    const mockPdfContent = `encrypted_document_${documentId}`
    const encoder = new TextEncoder()
    const mockPdfBytes = encoder.encode(mockPdfContent)

    // Create a new ArrayBuffer from the bytes
    const arrayBuffer = new ArrayBuffer(mockPdfBytes.length)
    const uint8Array = new Uint8Array(arrayBuffer)
    uint8Array.set(mockPdfBytes)

    return {
      data: arrayBuffer,
      mimeType: 'application/pdf',
    }
  }

  try {
    // eslint-disable-next-line no-console
    console.log('Decrypting documentId', documentId)

    const client = getBtClient()

    // Get document metadata first
    const document = await client.documents.get(documentId)

    // Determine MIME type from metadata or detect from content
    let mimeType = document.contentType || 'application/octet-stream'
    // eslint-disable-next-line no-console
    console.log('MIME type:', mimeType)

    const download = await client.documents.data.get(documentId)

    let arrayBuffer: ArrayBuffer

    if (download && typeof download === 'object' && 'stream' in download) {
      // For Basis Theory's stream response, collect chunks
      const stream = download.stream()

      const reader = stream.getReader()

      const chunks: Uint8Array[] = []
      let totalLength = 0

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        chunks.push(value)
        totalLength += value.length
      }

      // eslint-disable-next-line no-console
      console.log('Total chunks collected:', chunks.length)
      // eslint-disable-next-line no-console
      console.log('Total data size:', totalLength)

      // Combine all chunks into a single ArrayBuffer
      arrayBuffer = new ArrayBuffer(totalLength)
      const uint8Array = new Uint8Array(arrayBuffer)

      let offset = 0
      for (const chunk of chunks) {
        uint8Array.set(chunk, offset)
        offset += chunk.length
      }

      // Use detected MIME type if available, otherwise fall back to metadata
      if (mimeType === 'application/octet-stream') {
        mimeType = detectBinaryContentType(arrayBuffer)
      }

      // Check if the ArrayBuffer contains a data URL
      const firstBytes = new Uint8Array(arrayBuffer.slice(0, 30))
      const firstBytesString = String.fromCharCode(...Array.from(firstBytes))
      if (firstBytesString.startsWith('data:')) {
        // Use TextDecoder for large ArrayBuffers to avoid stack overflow
        const decoder = new TextDecoder('utf-8')
        const dataUrlString = decoder.decode(arrayBuffer)

        // Extract base64 from data URL
        const splitBase64 = dataUrlString.split(';')
        const encodedFile = splitBase64[1].split(',')[1]
        const contentType = splitBase64[0].split(':')[1] || mimeType

        // Convert base64 to ArrayBuffer
        const byteCharacters = atob(encodedFile)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }

        arrayBuffer = new Uint8Array(byteNumbers).buffer

        // Update MIME type to match the actual content
        mimeType = contentType
      }

      return {
        data: arrayBuffer,
        mimeType: mimeType,
      }
    } else if (typeof download === 'string') {
      // Handle case where Basis Theory returns a data URL (base64-encoded)
      const downloadString = download as string

      if (downloadString.startsWith('data:')) {
        // It's already a data URL, extract the base64 part
        const base64Data = downloadString
        const splitBase64 = base64Data.split(';')
        const encodedFile = splitBase64[1].split(',')[1]
        const contentType = splitBase64[0].split(':')[1] || 'application/pdf'

        // Convert base64 to ArrayBuffer
        const byteCharacters = atob(encodedFile)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }

        arrayBuffer = new Uint8Array(byteNumbers).buffer

        return {
          data: arrayBuffer,
          mimeType: contentType,
        }
      } else {
        // It's a plain base64 string, decode directly
        const byteCharacters = atob(downloadString)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }

        arrayBuffer = new Uint8Array(byteNumbers).buffer

        return {
          data: arrayBuffer,
          mimeType: 'application/pdf',
        }
      }
    } else {
      throw new Error('Download response does not have expected stream method or string format')
    }
  } catch (error) {
    console.error('Error decrypting document:', error)
    throw error
  }
}
